import throttle from 'lodash/throttle';
import {RefObject, useEffect, useMemo} from 'react';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {useWindowEventBlockerRef} from '../../../../../../common/useWindowEventBlockerRef.hook';
import {events} from '../constant/textViewerSagaEvents';
import {setCurrentPage} from '../state/textViewerSlice';
import {FrameSize} from '../type/FrameSize.interface';
import {calculateScrollViewerCurrentPage} from '../util/calculateScrollViewerCurrentPage';

/**
 * 스크롤 뷰어의 스크롤 위치를 조정해주는 로직을 담당하는 컴포넌트입니다.
 */
export function ScrollViewerScrollPositionAdjuster({
  productId,
  size,
  wrapperRef,
}: {
  productId: number;
  size: FrameSize;
  wrapperRef?: RefObject<HTMLDivElement>;
}) {
  const {
    isHandlingSeekBar,
    currentPage,
    currentPageCount,
    isContentLoaded,
    isDrawing,
    isPageCalculating,
  } = useTypedSelector(state => ({
    isHandlingSeekBar: state.textViewer.viewData[productId]?.isHandlingSeekBar,
    currentPage: state.textViewer.viewData[productId]?.currentPage,
    currentPageCount: state.textViewer.viewData[productId]?.currentPageCount,
    isContentLoaded: state.textViewer.viewData[productId]?.isContentLoaded,
    isDrawing: state.textViewer.viewData[productId]?.isDrawing,
    isPageCalculating: state.textViewer.viewData[productId]?.isPageCalculating,
  }));
  const dispatch = useTypedDispatch();

  // scrollInToView나 scrollTo 실행 시 뒤에 실행되는 onScroll을 막기위해 사용됩니다.
  const windowScrollEventBlockerRef = useWindowEventBlockerRef('scroll');

  useEffect(() => {
    // 실제로 시크바를 조작해서 페이지를 바꾸는 경우에만 스크롤 포지션을 조정해줍니다.
    if (
      isContentLoaded &&
      !isDrawing &&
      isHandlingSeekBar &&
      currentPage &&
      wrapperRef?.current
    ) {
      const div = wrapperRef.current;
      const wrapperStartY = div.offsetTop;
      const wrapperBottomY = div.offsetTop + div.offsetHeight;

      const pageHeight = size.height;
      const expectedPageScrollHeight = Math.min(
        wrapperStartY + pageHeight * (currentPage - 1),
        wrapperBottomY
      );
      window.scroll(0, expectedPageScrollHeight);

      // ScrollTextViewerProgressContainer에서 scroll 이벤트 핸들러로 핸들링해주고 있는 부분을 직접 처리합니다. (scroll 이벤트를 막으므로)
      dispatch(
        events.updateScrollViewerCurrentRateRequired({
          productId,
          wrapperStartY,
          wrapperBottomY,
        })
      );

      windowScrollEventBlockerRef.current = true;
    }
  }, [
    isHandlingSeekBar,
    currentPage,
    size,
    wrapperRef,
    isContentLoaded,
    isDrawing,
    windowScrollEventBlockerRef,
    dispatch,
    productId,
  ]);

  const trackPageChangeWhenScroll = useMemo(
    () =>
      throttle(() => {
        if (
          !isContentLoaded ||
          isDrawing ||
          isPageCalculating ||
          !currentPage ||
          !currentPageCount ||
          !wrapperRef?.current
        ) {
          return;
        }

        const div = wrapperRef.current;
        const wrapperStartY = div.offsetTop;
        const wrapperBottomY = div.offsetTop + div.offsetHeight;
        const pageHeight = size.height;

        const newCurrentPage = calculateScrollViewerCurrentPage({
          pageHeight,
          wrapperStartY,
          wrapperBottomY,
          currentScrollY: window.scrollY,
          currentPageCount,
        });

        if (currentPage !== newCurrentPage) {
          dispatch(setCurrentPage({productId, page: newCurrentPage}));
        }
      }, 100),
    [
      currentPage,
      currentPageCount,
      dispatch,
      isContentLoaded,
      isDrawing,
      isPageCalculating,
      productId,
      size.height,
      wrapperRef,
    ]
  );

  useEffect(() => {
    window.addEventListener('scroll', trackPageChangeWhenScroll);

    if (isContentLoaded) {
      trackPageChangeWhenScroll();
    }
    return () => {
      window.removeEventListener('scroll', trackPageChangeWhenScroll);
    };
  }, [isContentLoaded, trackPageChangeWhenScroll]);

  return null;
}
